// services/chatbotApi.js
// API service for chatbot backend communication

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

/**
 * Send a message to the chatbot
 * @param {string} message - User message
 * @param {string} conversationId - Conversation ID
 * @param {Array} documents - Uploaded documents
 * @returns {Promise<Object>} - Bot response
 */
export const sendMessage = async (message, conversationId, documents = []) => {
  try {
    // Phase 1: Mock response for testing
    // Replace this with actual API call in Phase 2
    
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    // Mock response based on message content
    const mockResponse = generateMockResponse(message, documents);

    return mockResponse;

    /* Phase 2: Uncomment this for real API
    const response = await fetch(`${API_BASE_URL}/chat/message`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        document_ids: documents.map(doc => doc.id)
      })
    });

    if (!response.ok) {
      throw new Error('Failed to send message');
    }

    return await response.json();
    */
  } catch (error) {
    console.error('Error in sendMessage:', error);
    throw error;
  }
};

/**
 * Upload a document
 * @param {File} file - File to upload
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} - Upload result
 */
export const uploadDocument = async (file, conversationId) => {
  try {
    // Phase 1: Mock upload
    await new Promise(resolve => setTimeout(resolve, 1000));

    return {
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type,
      url: URL.createObjectURL(file),
      status: 'success'
    };

    /* Phase 2: Uncomment for real API
    const formData = new FormData();
    formData.append('file', file);
    formData.append('conversation_id', conversationId);

    const response = await fetch(`${API_BASE_URL}/chat/upload`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      throw new Error('Failed to upload document');
    }

    return await response.json();
    */
  } catch (error) {
    console.error('Error in uploadDocument:', error);
    throw error;
  }
};

/**
 * Get conversation history
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<Object>} - Conversation data
 */
export const getConversation = async (conversationId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/conversations/${conversationId}`);
    
    if (!response.ok) {
      throw new Error('Failed to fetch conversation');
    }

    return await response.json();
  } catch (error) {
    console.error('Error in getConversation:', error);
    throw error;
  }
};

/**
 * Delete a conversation
 * @param {string} conversationId - Conversation ID
 * @returns {Promise<void>}
 */
export const deleteConversation = async (conversationId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/conversations/${conversationId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error('Failed to delete conversation');
    }
  } catch (error) {
    console.error('Error in deleteConversation:', error);
    throw error;
  }
};

/**
 * Delete a document
 * @param {string} documentId - Document ID
 * @returns {Promise<void>}
 */
export const deleteDocument = async (documentId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/chat/documents/${documentId}`, {
      method: 'DELETE'
    });

    if (!response.ok) {
      throw new Error('Failed to delete document');
    }
  } catch (error) {
    console.error('Error in deleteDocument:', error);
    throw error;
  }
};

// ============================================
// Mock Response Generator (Phase 1 only)
// ============================================

const generateMockResponse = (message, documents) => {
  const lowerMessage = message.toLowerCase();

  // Check for document-related queries
  if (documents.length > 0) {
    if (lowerMessage.includes('summarize') || lowerMessage.includes('summary')) {
      return {
        content: `I've analyzed ${documents.length} document(s). Here's a summary:\n\nThe documents appear to contain legal agreements and property information. Key points include:\n\n1. Property identification and location details\n2. Terms and conditions of the agreement\n3. Payment schedules and financial obligations\n4. Rights and responsibilities of parties involved\n\nWould you like me to focus on any specific aspect?`,
        sources: documents.slice(0, 2).map((doc, i) => ({
          document_name: doc.name,
          page: i + 1,
          excerpt: 'This is a sample excerpt from the document...',
          relevance_score: 0.95 - (i * 0.05)
        }))
      };
    }

    if (lowerMessage.includes('rera') || lowerMessage.includes('compliance')) {
      return {
        content: `Based on the uploaded documents, I'll check for RERA compliance:\n\n✓ Property registration status\n✓ Builder/developer information\n✓ Project approval details\n✓ Completion timeline\n\nNote: This is a preliminary analysis. Please consult with a legal professional for comprehensive compliance verification.`,
        sources: documents.slice(0, 1).map(doc => ({
          document_name: doc.name,
          page: 1,
          excerpt: 'RERA registration details...',
          relevance_score: 0.92
        }))
      };
    }

    if (lowerMessage.includes('risk') || lowerMessage.includes('issue')) {
      return {
        content: `I've identified potential areas of concern:\n\n⚠️ Areas requiring attention:\n1. Payment terms and conditions\n2. Possession timeline clauses\n3. Force majeure provisions\n4. Dispute resolution mechanisms\n\nThese points should be reviewed carefully with legal counsel.`,
        sources: documents.slice(0, 2).map((doc, i) => ({
          document_name: doc.name,
          page: 2 + i,
          excerpt: 'Relevant clause excerpt...',
          relevance_score: 0.88
        }))
      };
    }

    if (lowerMessage.includes('clause') || lowerMessage.includes('key')) {
      return {
        content: `Key clauses found in the documents:\n\n1. **Agreement Terms**: Duration and validity period\n2. **Payment Structure**: Installments and due dates\n3. **Property Specifications**: Area, location, and amenities\n4. **Cancellation Policy**: Terms for agreement termination\n5. **Legal Compliance**: RERA and local regulation references\n\nWould you like detailed information about any specific clause?`,
        sources: documents.map((doc, i) => ({
          document_name: doc.name,
          page: i + 1,
          excerpt: 'Key clause text...',
          relevance_score: 0.90 - (i * 0.02)
        }))
      };
    }
  }

  // General responses without documents
  if (lowerMessage.includes('hello') || lowerMessage.includes('hi')) {
    return {
      content: 'Hello! I\'m your Propsten Legal Assistant. I can help you analyze legal documents, check RERA compliance, identify risks, and answer questions about property agreements. Please upload your documents to get started.',
      sources: []
    };
  }

  if (lowerMessage.includes('help')) {
    return {
      content: 'I can assist you with:\n\n• Document analysis and summarization\n• RERA compliance checking\n• Risk identification in agreements\n• Clause explanation\n• Legal terminology clarification\n\nPlease upload your legal documents and ask specific questions about them.',
      sources: []
    };
  }

  // Default response
  return {
    content: documents.length > 0
      ? `I understand your question about "${message}". Based on the uploaded documents, I can provide detailed analysis. Could you be more specific about what aspect you'd like me to focus on?`
      : `I'd be happy to help with "${message}". However, I need you to upload relevant legal documents first so I can provide accurate, document-based answers.`,
    sources: []
  };
};

export default {
  sendMessage,
  uploadDocument,
  getConversation,
  deleteConversation,
  deleteDocument
};
